// ==================== ARRANGEMENT MOCKUP ENGINE ====================
// Takes a user's loop audio + arrangement sections and creates a
// full-song preview using Web Audio API processing.
//
// NOT AI music generation — just filters, volume, EQ automation,
// and synthesized FX (risers, impacts) applied to the user's own
// loop across the arrangement timeline.
//
// Output is watermarked (lo-fi, mono, periodic beep) so it's useful
// for planning but not usable as a finished track.

const WATERMARK_BEEP_INTERVAL_BARS = 8;
const WATERMARK_BEEP_FREQ = 1000;
const WATERMARK_BEEP_DURATION = 0.08;
const WATERMARK_BEEP_GAIN = 0.06;
const WATERMARK_LOWPASS_CAP = 8000;

// Element-to-frequency-band mapping for selective muting
const ELEMENT_BANDS = {
  'kick':       { type: 'lowshelf', freq: 100, gain: -40 },
  'sub-bass':   { type: 'lowshelf', freq: 80, gain: -40 },
  'bass':       { type: 'peaking', freq: 150, Q: 1.5, gain: -30 },
  'snare':      { type: 'peaking', freq: 1200, Q: 2, gain: -25 },
  'hi-hats':    { type: 'highshelf', freq: 8000, gain: -35 },
  'percussion': { type: 'peaking', freq: 4000, Q: 1.5, gain: -20 },
  'lead-synth': { type: 'peaking', freq: 2500, Q: 2, gain: -25 },
  'pad':        { type: 'peaking', freq: 800, Q: 0.8, gain: -20 },
  'vocal':      { type: 'peaking', freq: 3000, Q: 2, gain: -25 },
  'fx':         { type: 'highshelf', freq: 6000, gain: -20 },
  'guitar':     { type: 'peaking', freq: 1500, Q: 1.5, gain: -20 },
  'strings':    { type: 'peaking', freq: 1000, Q: 1.0, gain: -18 },
};

// Section-type processing presets
const SECTION_PRESETS = {
  intro: {
    filterType: 'lowpass',
    filterStart: 600,
    filterEnd: 2000,
    gainStart: 0.25,
    gainEnd: 0.45,
    highpass: 0,
    // Elements to REMOVE in this section (simulates stripping to pads/atmosphere)
    muteElements: ['kick', 'snare', 'hi-hats', 'percussion'],
    // Synthesized FX
    fx: [],
  },
  buildup: {
    filterType: 'lowpass',
    filterStart: 1500,
    filterEnd: 14000,
    gainStart: 0.5,
    gainEnd: 0.9,
    highpass: 0,
    muteElements: [],
    fx: ['riser', 'snare-roll'],
  },
  build: {
    filterType: 'lowpass',
    filterStart: 1500,
    filterEnd: 14000,
    gainStart: 0.5,
    gainEnd: 0.9,
    highpass: 0,
    muteElements: [],
    fx: ['riser', 'snare-roll'],
  },
  drop: {
    filterType: 'lowpass',
    filterStart: 18000,
    filterEnd: 18000,
    gainStart: 1.0,
    gainEnd: 1.0,
    highpass: 0,
    muteElements: [],
    fx: ['impact'],
  },
  chorus: {
    filterType: 'lowpass',
    filterStart: 16000,
    filterEnd: 16000,
    gainStart: 0.95,
    gainEnd: 0.95,
    highpass: 0,
    muteElements: [],
    fx: [],
  },
  verse: {
    filterType: 'lowpass',
    filterStart: 4000,
    filterEnd: 5000,
    gainStart: 0.55,
    gainEnd: 0.6,
    highpass: 0,
    muteElements: ['lead-synth', 'hi-hats'],
    fx: [],
  },
  breakdown: {
    filterType: 'lowpass',
    filterStart: 3000,
    filterEnd: 4000,
    gainStart: 0.4,
    gainEnd: 0.5,
    highpass: 150,
    muteElements: ['kick', 'snare', 'percussion', 'bass'],
    fx: [],
  },
  bridge: {
    filterType: 'lowpass',
    filterStart: 5000,
    filterEnd: 6000,
    gainStart: 0.5,
    gainEnd: 0.6,
    highpass: 0,
    muteElements: ['lead-synth', 'percussion'],
    fx: [],
  },
  outro: {
    filterType: 'lowpass',
    filterStart: 4000,
    filterEnd: 400,
    gainStart: 0.6,
    gainEnd: 0.0,
    highpass: 0,
    muteElements: ['snare', 'lead-synth'],
    fx: ['fade'],
  },
  'pre-chorus': {
    filterType: 'lowpass',
    filterStart: 3000,
    filterEnd: 10000,
    gainStart: 0.55,
    gainEnd: 0.8,
    highpass: 0,
    muteElements: [],
    fx: ['riser'],
  },
  hook: {
    filterType: 'lowpass',
    filterStart: 16000,
    filterEnd: 16000,
    gainStart: 0.95,
    gainEnd: 0.95,
    highpass: 0,
    muteElements: [],
    fx: [],
  },
};

// Fallback preset based on energy level
function getPresetForSection(section) {
  const type = (section.sectionType || section.type || '').toLowerCase();
  if (SECTION_PRESETS[type]) return SECTION_PRESETS[type];

  const energy = section.energy || 0.5;
  if (energy >= 0.8) return SECTION_PRESETS.drop;
  if (energy >= 0.6) return SECTION_PRESETS.chorus;
  if (energy >= 0.4) return SECTION_PRESETS.verse;
  if (energy >= 0.2) return SECTION_PRESETS.breakdown;
  return SECTION_PRESETS.intro;
}

// Determine which elements to mute based on section's element list vs the loop's full elements
function getElementsToMute(section, preset) {
  const sectionElements = section.elements || [];
  const loopElements = section._allElements || [];

  // If section has an explicit element list, mute anything NOT in it
  if (sectionElements.length > 0 && loopElements.length > 0) {
    return loopElements.filter(el => !sectionElements.includes(el));
  }

  // Otherwise fall back to preset's default muting
  return preset.muteElements || [];
}

export class ArrangementMockupEngine {
  constructor() {
    this.audioContext = null;
    this.audioBuffer = null;
    this.isPlaying = false;
    this.isPaused = false;
    this.sourceNodes = [];
    this.scheduledNodes = [];
    this.startTime = 0;
    this.pauseOffset = 0;
    this.totalDuration = 0;
    this.sections = [];
    this.allLoopElements = [];
    this.bpm = 120;
    this.onProgress = null;
    this.onSectionChange = null;
    this.progressInterval = null;
    this.masterGain = null;
  }

  async loadAudio(audioUrl) {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }

    const response = await fetch(audioUrl);
    const arrayBuffer = await response.arrayBuffer();
    this.audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
  }

  prepare(sections, bpm) {
    this.sections = sections;
    this.bpm = bpm;
    this.totalDuration = this._calculateTotalDuration();

    // Collect the full element list from the highest-energy section (likely the drop)
    const dropSection = sections.reduce((best, s) =>
      (s.energy || 0) > (best.energy || 0) ? s : best, sections[0]);
    this.allLoopElements = dropSection.elements || [];

    // Tag each section with the full element list for comparison
    this.sections = sections.map(s => ({ ...s, _allElements: this.allLoopElements }));
  }

  _barsToDuration(bars) {
    return (bars * 4 * 60) / this.bpm;
  }

  _calculateTotalDuration() {
    return this.sections.reduce((total, s) => total + this._barsToDuration(s.bars || 8), 0);
  }

  _getCurrentSection(elapsed) {
    let cumulative = 0;
    for (let i = 0; i < this.sections.length; i++) {
      const dur = this._barsToDuration(this.sections[i].bars || 8);
      if (elapsed < cumulative + dur) {
        return {
          index: i,
          section: this.sections[i],
          sectionStart: cumulative,
          sectionEnd: cumulative + dur,
          sectionProgress: (elapsed - cumulative) / dur,
        };
      }
      cumulative += dur;
    }
    return null;
  }

  play(offset = 0) {
    if (!this.audioBuffer || !this.audioContext) return;
    if (this.isPlaying && !this.isPaused) return;

    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }

    this._stopAllNodes();

    const startOffset = offset || this.pauseOffset;
    this.startTime = this.audioContext.currentTime - startOffset;
    this.isPlaying = true;
    this.isPaused = false;

    this._schedulePlayback(startOffset);
    this._startProgressTracking();
  }

  pause() {
    if (!this.isPlaying || this.isPaused) return;
    this.pauseOffset = this.audioContext.currentTime - this.startTime;
    this.isPaused = true;
    this._stopAllNodes();
    this._stopProgressTracking();
  }

  stop() {
    this.isPlaying = false;
    this.isPaused = false;
    this.pauseOffset = 0;
    this._stopAllNodes();
    this._stopProgressTracking();
    if (this.onProgress) this.onProgress(0, 0, null);
  }

  seekTo(time) {
    const wasPlaying = this.isPlaying && !this.isPaused;
    this.stop();
    this.pauseOffset = Math.max(0, Math.min(time, this.totalDuration));
    if (wasPlaying) {
      this.play(this.pauseOffset);
    } else {
      if (this.onProgress) {
        const info = this._getCurrentSection(this.pauseOffset);
        this.onProgress(this.pauseOffset, this.pauseOffset / this.totalDuration, info);
      }
    }
  }

  destroy() {
    this.stop();
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    this.audioBuffer = null;
  }

  _schedulePlayback(startOffset) {
    const ctx = this.audioContext;
    const loopDuration = this.audioBuffer.duration;

    // Master output chain: watermark low-pass → mono merge → destination
    const watermarkFilter = ctx.createBiquadFilter();
    watermarkFilter.type = 'lowpass';
    watermarkFilter.frequency.value = WATERMARK_LOWPASS_CAP;
    watermarkFilter.Q.value = 0.7;

    // Force mono for watermark
    const splitter = ctx.createChannelSplitter(2);
    const merger = ctx.createChannelMerger(1);
    const monoGain = ctx.createGain();
    monoGain.gain.value = 0.8;

    watermarkFilter.connect(splitter);
    splitter.connect(merger, 0, 0);
    if (this.audioBuffer.numberOfChannels > 1) {
      splitter.connect(merger, 1, 0);
    }
    merger.connect(monoGain);
    monoGain.connect(ctx.destination);

    this.masterGain = monoGain;

    // Schedule sections
    let sectionTime = 0;
    for (let i = 0; i < this.sections.length; i++) {
      const section = this.sections[i];
      const sectionDuration = this._barsToDuration(section.bars || 8);

      if (sectionTime + sectionDuration <= startOffset) {
        sectionTime += sectionDuration;
        continue;
      }

      const offsetIntoSection = Math.max(0, startOffset - sectionTime);
      const remainingDuration = sectionDuration - offsetIntoSection;
      const when = ctx.currentTime + Math.max(0, sectionTime - startOffset);

      // Check if next section is a drop/chorus for impact scheduling
      const nextSection = this.sections[i + 1];
      const nextIsHigh = nextSection && (nextSection.energy || 0) >= 0.8;

      this._scheduleSectionPlayback(
        ctx, watermarkFilter, section, when, offsetIntoSection, remainingDuration, loopDuration, nextIsHigh
      );

      sectionTime += sectionDuration;
    }

    // Schedule watermark beeps
    this._scheduleWatermarkBeeps(ctx, monoGain, startOffset);

    // Schedule auto-stop
    const remaining = this.totalDuration - startOffset;
    const stopTimer = setTimeout(() => this.stop(), remaining * 1000 + 200);
    this.scheduledNodes.push({ stop: () => clearTimeout(stopTimer) });
  }

  _scheduleSectionPlayback(ctx, destination, section, when, offsetIntoSection, duration, loopDuration, nextIsHigh) {
    const preset = getPresetForSection(section);

    // Per-section filter (main low-pass sweep)
    const filter = ctx.createBiquadFilter();
    filter.type = preset.filterType;
    filter.Q.value = 1.5;
    filter.frequency.setValueAtTime(preset.filterStart, when);
    filter.frequency.linearRampToValueAtTime(preset.filterEnd, when + duration);

    // Per-section gain
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(preset.gainStart, when);
    gain.gain.linearRampToValueAtTime(preset.gainEnd, when + duration);

    // Build the filter chain: source → [element EQs] → [highpass] → lowpass → gain → destination
    let chainHead = filter;

    // Optional high-pass (for breakdowns — remove kick)
    if (preset.highpass > 0) {
      const hp = ctx.createBiquadFilter();
      hp.type = 'highpass';
      hp.frequency.value = preset.highpass;
      hp.Q.value = 0.7;
      hp.connect(filter);
      chainHead = hp;
    }

    // Element-based EQ cuts — selectively remove frequency bands
    const elementsToMute = getElementsToMute(section, preset);
    let eqHead = chainHead;
    for (const element of elementsToMute) {
      const band = ELEMENT_BANDS[element];
      if (!band) continue;

      const eq = ctx.createBiquadFilter();
      eq.type = band.type;
      eq.frequency.value = band.freq;
      eq.gain.value = band.gain;
      if (band.Q) eq.Q.value = band.Q;

      eq.connect(eqHead);
      eqHead = eq;
    }

    filter.connect(gain);
    gain.connect(destination);

    // Loop the audio buffer across the section duration
    const totalLoops = Math.ceil(duration / loopDuration) + 1;
    let loopOffset = offsetIntoSection % loopDuration;
    let scheduleTime = when;

    for (let j = 0; j < totalLoops; j++) {
      const source = ctx.createBufferSource();
      source.buffer = this.audioBuffer;

      const clipDuration = Math.min(
        loopDuration - loopOffset,
        (when + duration) - scheduleTime
      );

      if (clipDuration <= 0) break;

      source.connect(eqHead);
      source.start(scheduleTime, loopOffset, clipDuration);

      this.sourceNodes.push(source);
      scheduleTime += clipDuration;
      loopOffset = 0;
    }

    // Schedule synthesized FX for this section
    const fxList = preset.fx || [];
    this._scheduleSectionFX(ctx, destination, fxList, when, duration, nextIsHigh);
  }

  _scheduleSectionFX(ctx, destination, fxList, when, duration, nextIsHigh) {
    for (const fx of fxList) {
      switch (fx) {
        case 'riser':
          this._scheduleRiser(ctx, destination, when, duration);
          break;
        case 'impact':
          this._scheduleImpact(ctx, destination, when);
          break;
        case 'snare-roll':
          this._scheduleSnareRoll(ctx, destination, when, duration);
          break;
      }
    }

    // If next section is a high-energy drop, add impact at the transition
    if (nextIsHigh) {
      this._scheduleImpact(ctx, destination, when + duration);
    }
  }

  // White noise riser that sweeps up over the section duration
  _scheduleRiser(ctx, destination, when, duration) {
    const bufferSize = ctx.sampleRate * duration;
    if (bufferSize <= 0 || bufferSize > ctx.sampleRate * 60) return;

    const noiseBuffer = ctx.createBuffer(1, Math.min(bufferSize, ctx.sampleRate * 30), ctx.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      data[i] = (Math.random() * 2 - 1);
    }

    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuffer;

    // Sweep a band-pass filter upward
    const riserFilter = ctx.createBiquadFilter();
    riserFilter.type = 'bandpass';
    riserFilter.Q.value = 5;
    riserFilter.frequency.setValueAtTime(200, when);
    riserFilter.frequency.exponentialRampToValueAtTime(8000, when + duration);

    // Ramp volume up
    const riserGain = ctx.createGain();
    riserGain.gain.setValueAtTime(0.0, when);
    riserGain.gain.linearRampToValueAtTime(0.12, when + duration * 0.8);
    riserGain.gain.linearRampToValueAtTime(0.18, when + duration);

    noise.connect(riserFilter);
    riserFilter.connect(riserGain);
    riserGain.connect(destination);

    noise.start(when);
    noise.stop(when + duration);
    this.scheduledNodes.push(noise);
  }

  // Short impact/boom at the start of a section (sub hit + noise burst)
  _scheduleImpact(ctx, destination, when) {
    // Sub boom
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(80, when);
    osc.frequency.exponentialRampToValueAtTime(30, when + 0.3);

    const impactGain = ctx.createGain();
    impactGain.gain.setValueAtTime(0.4, when);
    impactGain.gain.exponentialRampToValueAtTime(0.001, when + 0.4);

    osc.connect(impactGain);
    impactGain.connect(destination);
    osc.start(when);
    osc.stop(when + 0.5);
    this.scheduledNodes.push(osc);

    // Noise burst layered on top
    const burstLength = 0.08;
    const burstBuffer = ctx.createBuffer(1, ctx.sampleRate * burstLength, ctx.sampleRate);
    const burstData = burstBuffer.getChannelData(0);
    for (let i = 0; i < burstData.length; i++) {
      burstData[i] = (Math.random() * 2 - 1) * (1 - i / burstData.length);
    }

    const burst = ctx.createBufferSource();
    burst.buffer = burstBuffer;

    const burstGain = ctx.createGain();
    burstGain.gain.setValueAtTime(0.15, when);
    burstGain.gain.exponentialRampToValueAtTime(0.001, when + burstLength);

    burst.connect(burstGain);
    burstGain.connect(destination);
    burst.start(when);
    burst.stop(when + burstLength);
    this.scheduledNodes.push(burst);

    // Brief silence before impact (1/16 note gap for drama)
    // This is achieved by the gain being 0 in the transition moment
  }

  // Snare roll that accelerates toward the end of the section
  _scheduleSnareRoll(ctx, destination, when, duration) {
    // Start with quarter notes, accelerate to 32nd notes in the last 25%
    const rollStart = when + duration * 0.5;
    const rollDuration = duration * 0.5;
    const rollEnd = when + duration;

    const hitDuration = 0.03;
    let time = rollStart;
    let interval = (60 / this.bpm) / 2; // start at 8th notes

    while (time < rollEnd) {
      const progress = (time - rollStart) / rollDuration;

      // Accelerate: 8th notes → 16th → 32nd
      if (progress > 0.7) interval = (60 / this.bpm) / 8;
      else if (progress > 0.4) interval = (60 / this.bpm) / 4;

      // Noise hit (simulates snare)
      const hitBuffer = ctx.createBuffer(1, ctx.sampleRate * hitDuration, ctx.sampleRate);
      const hitData = hitBuffer.getChannelData(0);
      for (let i = 0; i < hitData.length; i++) {
        hitData[i] = (Math.random() * 2 - 1) * (1 - i / hitData.length);
      }

      const hit = ctx.createBufferSource();
      hit.buffer = hitBuffer;

      // Band-pass to sound snare-ish
      const hitFilter = ctx.createBiquadFilter();
      hitFilter.type = 'bandpass';
      hitFilter.frequency.value = 1200;
      hitFilter.Q.value = 1;

      const hitGain = ctx.createGain();
      const volume = 0.04 + progress * 0.1; // crescendo
      hitGain.gain.value = volume;

      hit.connect(hitFilter);
      hitFilter.connect(hitGain);
      hitGain.connect(destination);
      hit.start(time);
      hit.stop(time + hitDuration);
      this.scheduledNodes.push(hit);

      time += interval;
    }
  }

  _scheduleWatermarkBeeps(ctx, destination, startOffset) {
    const barDuration = (4 * 60) / this.bpm;
    const totalBars = this.sections.reduce((sum, s) => sum + (s.bars || 8), 0);

    for (let bar = WATERMARK_BEEP_INTERVAL_BARS; bar < totalBars; bar += WATERMARK_BEEP_INTERVAL_BARS) {
      const beepTime = bar * barDuration;
      if (beepTime <= startOffset) continue;

      const when = ctx.currentTime + (beepTime - startOffset);

      const osc = ctx.createOscillator();
      const beepGain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.value = WATERMARK_BEEP_FREQ;
      beepGain.gain.setValueAtTime(0, when);
      beepGain.gain.linearRampToValueAtTime(WATERMARK_BEEP_GAIN, when + 0.01);
      beepGain.gain.setValueAtTime(WATERMARK_BEEP_GAIN, when + WATERMARK_BEEP_DURATION - 0.01);
      beepGain.gain.linearRampToValueAtTime(0, when + WATERMARK_BEEP_DURATION);

      osc.connect(beepGain);
      beepGain.connect(destination);
      osc.start(when);
      osc.stop(when + WATERMARK_BEEP_DURATION);

      this.scheduledNodes.push(osc);
    }
  }

  _stopAllNodes() {
    for (const source of this.sourceNodes) {
      try { source.stop(); } catch (e) { /* already stopped */ }
    }
    for (const node of this.scheduledNodes) {
      try { node.stop(); } catch (e) { /* already stopped */ }
    }
    this.sourceNodes = [];
    this.scheduledNodes = [];
  }

  _startProgressTracking() {
    this._stopProgressTracking();
    this.progressInterval = setInterval(() => {
      if (!this.isPlaying || this.isPaused) return;
      const elapsed = this.audioContext.currentTime - this.startTime;
      const progress = elapsed / this.totalDuration;
      const info = this._getCurrentSection(elapsed);
      if (this.onProgress) this.onProgress(elapsed, Math.min(1, progress), info);
    }, 50);
  }

  _stopProgressTracking() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }
}
